import { Box, Text, Menu, MenuItem, Button } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  ShoppingCartOutlined,
  ArrowDropDown,
  FileDownloadOutlined,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';

import {
  PaginationControls,
  PaginationOverlay,
  PacerRedirectModal,
  InlineLoading,
  ErrorStateContainer,
  EmptyStateContainer,
  DocketEntriesWrapper,
  DescriptionContentBox,
} from '../../../shared';
import {
  getDownloadButtonGroupStyles,
  getDownloadButtonStyles,
  getDropdownButtonStyles,
} from '../../../shared/components';
import { useDocketEntriesData } from '../../../shared/hooks';
import { DocketEntry, RecapDocument } from '../../../types';
import {
  useDocketEntriesPagination,
  formatSimpleDate,
  isMainDocument,
} from '../../../utils';

import {
  DocketEntriesContainer,
  TableHeader,
  HeaderCell,
  HeaderCellText,
  HeaderCellDescription,
  SortIcon,
  Divider,
  TableRow,
  DocketNumberCell,
  DateCell,
  DescriptionCell,
  DescriptionCellNoEntryNumber,
  DescriptionText,
  BuyOnPacerButton,
  BuyOnPacerIcon,
  BuyOnPacerText,
  DocumentItemContainer,
  DocumentItem,
  DocumentRow,
  DocumentTitleContainer,
  DocumentActionsContainer,
  AttachmentRow,
  AttachmentTitle,
  AttachmentText,
} from './components';

interface DocketEntriesProps {
  docketId: string;
  pacerCaseId: string;
  pacerCourtId?: string | null;
  onCountChange: (count: number) => void;
}

export const DocketEntries = ({
  docketId,
  pacerCaseId,
  pacerCourtId,
  onCountChange,
}: DocketEntriesProps) => {
  const pacerHost = pacerCourtId
    ? `ecf.${pacerCourtId}.uscourts.gov`
    : 'ecf.wawd.uscourts.gov';
  const { currentPage, handlePreviousPage, handleNextPage } =
    useDocketEntriesPagination();

  const {
    docketEntries,
    effectiveDocketEntries,
    isLoading,
    isPaginationLoading,
    error,
  } = useDocketEntriesData(docketId, currentPage);

  const [isPacerModalOpen, setIsPacerModalOpen] = useState<boolean>(false);
  const [pacerUrlToOpen, setPacerUrlToOpen] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentDocId, setCurrentDocId] = useState<string | null>(null);
  const [currentEntryNumber, setCurrentEntryNumber] = useState<number | null>(
    null
  );
  const { isMobile } = useScreenSize();

  useEffect(() => {
    const count = effectiveDocketEntries?.count || docketEntries.length;
    onCountChange(count);
  }, [effectiveDocketEntries?.count, docketEntries.length, onCountChange]);

  const handleBuyOnPacer = (
    pacerDocId: string,
    entryNumber?: number | null
  ) => {
    let url = '';

    if (pacerDocId) {
      url = `https://${pacerHost}/doc1/${pacerDocId}?caseId=${pacerCaseId}`;
    } else if (entryNumber !== null && entryNumber !== undefined) {
      url = `https://${pacerHost}/cgi-bin/show_case_doc?${entryNumber},${pacerCaseId},,,`;
    }

    if (url) {
      setPacerUrlToOpen(url);
      setIsPacerModalOpen(true);

      if (anchorEl) {
        handleCloseMenu();
      }
    }
  };

  const handleContinueToPacer = () => {
    if (pacerUrlToOpen) {
      window.open(pacerUrlToOpen, '_blank', 'noopener,noreferrer');
    }
    setIsPacerModalOpen(false);
  };

  const openDocumentInNewTab = (url: string): void => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    docId: string,
    entryNumber?: number | null
  ) => {
    setAnchorEl(event.currentTarget);
    setCurrentDocId(docId);
    setCurrentEntryNumber(entryNumber || null);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setCurrentDocId(null);
    setCurrentEntryNumber(null);
  };

  const renderMainDocumentItem = (doc: RecapDocument, entry: DocketEntry) => {
    const shouldShowBuyButton = entry.entry_number !== null;

    return (
      <DocumentRow key={doc.id}>
        <DocumentTitleContainer>
          <AttachmentText>
            {doc.description?.trim()
              ? `Main Document - ${doc.description}`
              : 'Main Document'}
          </AttachmentText>
        </DocumentTitleContainer>

        <DocumentActionsContainer>
          {doc.is_available ? (
            <Box sx={(theme) => getDownloadButtonGroupStyles(theme)}>
              <Button
                component="a"
                href={doc.filepath_ia || '#'}
                size="small"
                variant="contained"
                color="primary"
                startIcon={<FileDownloadOutlined fontSize="small" />}
                onClick={(e) => {
                  e.preventDefault();
                  if (doc.filepath_ia) {
                    openDocumentInNewTab(doc.filepath_ia);
                  }
                }}
                title="Download document"
                sx={(theme) => getDownloadButtonStyles(theme)}
              >
                <Text fontSize="0.7rem" className="button-text">
                  Download PDF
                </Text>
              </Button>
              <Button
                size="small"
                variant="contained"
                color="primary"
                sx={(theme) => getDropdownButtonStyles(theme)}
                onClick={(e) =>
                  handleOpenMenu(e, doc.pacer_doc_id, entry.entry_number)
                }
              >
                <KeyboardArrowDown fontSize="small" />
              </Button>
            </Box>
          ) : (
            shouldShowBuyButton && (
              <BuyOnPacerButton
                onClick={() =>
                  handleBuyOnPacer(doc.pacer_doc_id, entry.entry_number)
                }
              >
                <BuyOnPacerIcon>
                  <ShoppingCartOutlined />
                </BuyOnPacerIcon>
                <BuyOnPacerText>Buy on PACER</BuyOnPacerText>
              </BuyOnPacerButton>
            )
          )}
        </DocumentActionsContainer>
      </DocumentRow>
    );
  };

  const renderAttachmentItem = (doc: RecapDocument, entry: DocketEntry) => {
    const shouldShowBuyButton = entry.entry_number !== null;
    const attachmentText = isMobile ? 'Att' : 'Attachment';

    return (
      <AttachmentRow key={doc.id}>
        <AttachmentTitle>
          <AttachmentText>
            {doc.attachment_number !== null
              ? `${attachmentText} ${doc.attachment_number} - ${doc.description}`
              : doc.description}
          </AttachmentText>
        </AttachmentTitle>

        <DocumentActionsContainer>
          {shouldShowBuyButton && (
            <BuyOnPacerButton
              onClick={() =>
                handleBuyOnPacer(doc.pacer_doc_id, entry.entry_number)
              }
            >
              <BuyOnPacerIcon>
                <ShoppingCartOutlined />
              </BuyOnPacerIcon>
              <BuyOnPacerText>Buy on PACER</BuyOnPacerText>
            </BuyOnPacerButton>
          )}
        </DocumentActionsContainer>
      </AttachmentRow>
    );
  };

  const renderDocumentItem = (doc: RecapDocument, entry: DocketEntry) => {
    if (isMainDocument(doc)) {
      return renderMainDocumentItem(doc, entry);
    }
    if (entry.entry_number) {
      return renderAttachmentItem(doc, entry);
    }
    return (
      <AttachmentRow key={doc.id}>
        <AttachmentTitle>
          <Text variant="body2" sx={{ color: 'text.primary' }}>
            {doc.description}
          </Text>
        </AttachmentTitle>
      </AttachmentRow>
    );
  };

  if (isLoading && !isPaginationLoading) {
    return <InlineLoading />;
  }

  if (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Failed to load docket entries';
    return (
      <ErrorStateContainer>
        <Text color="error">Error loading docket entries: {errorMessage}</Text>
      </ErrorStateContainer>
    );
  }

  return (
    <DocketEntriesWrapper>
      <DocketEntriesContainer>
        <TableHeader>
          <HeaderCell>
            <HeaderCellText>Docket #</HeaderCellText>
            <SortIcon>
              <ArrowDropDown />
            </SortIcon>
          </HeaderCell>
          <HeaderCell style={{ width: '70px' }}>
            <HeaderCellText>Date</HeaderCellText>
            <SortIcon>
              <ArrowDropDown />
            </SortIcon>
          </HeaderCell>
          <HeaderCellDescription>
            <HeaderCellText>Description</HeaderCellText>
            <SortIcon>
              <ArrowDropDown />
            </SortIcon>
          </HeaderCellDescription>
        </TableHeader>

        <Divider />

        {docketEntries.length > 0 ? (
          docketEntries.map((entry: DocketEntry, index: number) => {
            const isAlternate = index % 2 === 1;
            const CellWrapper = entry.entry_number
              ? DescriptionCell
              : DescriptionCellNoEntryNumber;
            const descriptionContent = (
              <DescriptionContentBox>
                <DescriptionText>{entry.description}</DescriptionText>
                {entry.recap_documents && entry.recap_documents.length > 0 && (
                  <DocumentItemContainer>
                    {entry.recap_documents.map((doc: RecapDocument) => (
                      <DocumentItem key={doc.id}>
                        {renderDocumentItem(doc, entry)}
                      </DocumentItem>
                    ))}
                  </DocumentItemContainer>
                )}
              </DescriptionContentBox>
            );

            return (
              <Box key={entry.id}>
                <TableRow isAlternate={isAlternate}>
                  <DocketNumberCell>
                    {entry.entry_number || 'N/A'}
                  </DocketNumberCell>
                  <DateCell>{formatSimpleDate(entry.date_filed)}</DateCell>
                  <CellWrapper>{descriptionContent}</CellWrapper>
                </TableRow>
                {index < docketEntries.length - 1 && <Divider />}
              </Box>
            );
          })
        ) : (
          <EmptyStateContainer>
            <Text variant="body2" color="text.secondary">
              No docket entries found
            </Text>
          </EmptyStateContainer>
        )}
      </DocketEntriesContainer>

      <PaginationControls
        hasNext={!!effectiveDocketEntries?.next}
        hasPrevious={!!effectiveDocketEntries?.previous}
        isLoading={isPaginationLoading}
        onNext={() => handleNextPage(effectiveDocketEntries?.next || null)}
        onPrevious={() =>
          handlePreviousPage(effectiveDocketEntries?.previous || null)
        }
      />

      <PaginationOverlay isVisible={isPaginationLoading} />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {currentDocId && (
          <MenuItem
            onClick={() => {
              handleBuyOnPacer(currentDocId, currentEntryNumber);
              handleCloseMenu();
            }}
          >
            Buy on PACER
          </MenuItem>
        )}
      </Menu>

      <PacerRedirectModal
        open={isPacerModalOpen}
        onClose={() => setIsPacerModalOpen(false)}
        onContinue={handleContinueToPacer}
      />
    </DocketEntriesWrapper>
  );
};
